<template>
  <nav
    class="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-1"
    :aria-label="I18n.t('common.pagination.label')"
  >
    <button
      type="button"
      :class="edgeClasses(currentPage === 1)"
      :disabled="currentPage === 1"
      @click="previousPage"
    >
      <i class="fa fa-chevron-left tw-text-[10px]" aria-hidden="true" />
      {{ I18n.t('common.pagination.previous') }}
    </button>
    <button
      v-if="startingAroundPage > 1"
      type="button"
      :class="PAGE_CLASSES"
      @click="changePage(1)"
    >
      1
    </button>
    <span v-if="startingAroundPage > 2" :class="ELLIPSIS_CLASSES"
      >&hellip;</span
    >
    <template v-if="startingAroundPage < currentPage">
      <button
        v-for="i in currentPage - startingAroundPage"
        :key="i + startingAroundPage - 1"
        type="button"
        :class="PAGE_CLASSES"
        @click="changePage(i + startingAroundPage - 1)"
      >
        {{ i + startingAroundPage - 1 }}
      </button>
    </template>
    <span :class="ACTIVE_PAGE_CLASSES" aria-current="page">
      {{ currentPage }}
    </span>
    <template v-if="endingAroundPage > currentPage">
      <button
        v-for="i in endingAroundPage - currentPage"
        :key="i + currentPage"
        type="button"
        :class="PAGE_CLASSES"
        @click="changePage(i + currentPage)"
      >
        {{ i + currentPage }}
      </button>
    </template>
    <span v-if="endingAroundPage < totalPages - 1" :class="ELLIPSIS_CLASSES">
      &hellip;
    </span>
    <button
      v-if="endingAroundPage < totalPages"
      type="button"
      :class="PAGE_CLASSES"
      @click="changePage(totalPages)"
    >
      {{ totalPages }}
    </button>
    <button
      type="button"
      :class="edgeClasses(currentPage === totalPages)"
      :disabled="currentPage === totalPages"
      @click="nextPage"
    >
      {{ I18n.t('common.pagination.next') }}
      <i class="fa fa-chevron-right tw-text-[10px]" aria-hidden="true" />
    </button>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({ name: 'SfPaginationDs' })

interface Props {
  currentPage: number
  totalPages: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ 'change-page': [number] }>()

const I18n = useI18n()

const BOX_CLASSES = [
  'tw-flex',
  'tw-h-7',
  'tw-min-w-7',
  'tw-items-center',
  'tw-justify-center',
  'tw-gap-1',
  'tw-rounded-md',
  'tw-px-[9px]',
  'tw-text-xs',
  'tw-font-medium',
  'tw-text-sf-slate',
]

// A browser gives every button a grey face, an outset border and a font of its
// own. The transparent border keeps the row from shifting when the active box
// appears.
const PAGE_CLASSES = [
  ...BOX_CLASSES,
  'tw-border',
  'tw-border-solid',
  'tw-border-transparent',
  'tw-bg-transparent',
  'tw-font-sans',
]

const ACTIVE_PAGE_CLASSES = [
  ...BOX_CLASSES,
  'tw-border',
  'tw-border-solid',
  'tw-border-sf-slate-lighter-2',
  'tw-bg-white',
  'tw-shadow-sm',
]

const ELLIPSIS_CLASSES = ['tw-px-1', 'tw-text-xs', 'tw-text-sf-slate']

const DISABLED_EDGE_CLASSES = [
  'tw-cursor-default',
  'tw-text-sf-gray-lighter',
  'hover:tw-no-underline',
  'hover:tw-text-sf-gray-lighter',
]

const edgeClasses = (disabled: boolean): string[] =>
  disabled ? [...PAGE_CLASSES, ...DISABLED_EDGE_CLASSES] : PAGE_CLASSES

const startingAroundPage = computed(() => Math.max(1, props.currentPage - 2))
const endingAroundPage = computed(() =>
  Math.min(props.totalPages, props.currentPage + 2)
)

function changePage(page: number) {
  emit('change-page', page)
}

function previousPage() {
  if (props.currentPage > 1) {
    changePage(props.currentPage - 1)
  }
}

function nextPage() {
  if (props.currentPage < props.totalPages) {
    changePage(props.currentPage + 1)
  }
}
</script>
